import { useState } from "react";
import type { FormEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da22cl5v7gurs51ef4cg";

export default function Contact() {
  useSeo(
    "联系扑飞漫画网 - 合作与反馈",
    "联系我们提交合作意向、功能建议或问题反馈。扑飞漫画网期待听到你的声音，工作日 9:00-18:00 在线。"
  );

  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const contactSchema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系扑飞漫画网",
    description: "扑飞漫画网联系表单与联系方式。",
  };

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Anti-spam honeypot
    const honeypot = data.get("website_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setStatus("success");
      form.reset();
      return;
    }

    setSubmitting(true);
    setFormError("");
    try {
      const payload = new URLSearchParams();
      for (const [key, value] of data.entries()) {
        if (key === "website_alt") continue;
        payload.append(key, String(value));
      }
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let code = "";
      let message = "";
      try {
        const parsed = JSON.parse(responseText);
        code = parsed?.code ?? "";
        message = parsed?.meta?.message ?? parsed?.message ?? parsed?.meta?.detail ?? "";
      } catch {
        /* keep defaults */
      }
      if (res.ok && code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          message && !/spam/i.test(message) ? message : "提交失败，请稍后重试或通过邮箱联系我们。"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <script type="application/ld+json">{JSON.stringify(contactSchema)}</script>
        <section className="border-b border-background-200/70 bg-background-100/70">
          <div className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
            <nav aria-label="面包屑" className="mb-4 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500">首页</Link>
              <span className="mx-2">/</span>
              <span className="text-foreground-800">联系我们</span>
            </nav>
            <h1 className="text-3xl leading-snug md:text-5xl">联系我们</h1>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
              内容合作、功能建议或问题反馈，都可以通过下面的表单告诉我们。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
            <div className="space-y-5">
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <h2 className="mt-3 font-heading text-base text-foreground-950">邮箱</h2>
                <p className="mt-1 text-sm text-foreground-700">contact@pufeimanga.example</p>
              </div>
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-xl text-secondary-700">
                  <i className="ri-time-line" />
                </span>
                <h2 className="mt-3 font-heading text-base text-foreground-950">在线时间</h2>
                <p className="mt-1 text-sm text-foreground-700">工作日 9:00 - 18:00</p>
              </div>
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-100 text-xl text-accent-700">
                  <i className="ri-community-line" />
                </span>
                <h2 className="mt-3 font-heading text-base text-foreground-950">社区</h2>
                <p className="mt-1 text-sm text-foreground-700">关注我们的社交账号，第一时间获取更新。</p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
                {status === "success" ? (
                  <div className="flex flex-col items-center py-12 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
                      <i className="ri-check-line" />
                    </span>
                    <h2 className="mt-4 font-heading text-xl text-foreground-950">提交成功</h2>
                    <p className="mt-2 text-sm text-foreground-600">感谢你的反馈，我们会尽快与你联系。</p>
                    <button
                      type="button"
                      onClick={() => setStatus("idle")}
                      className="mt-6 whitespace-nowrap rounded-md border border-background-200 px-5 py-2 text-sm font-semibold text-foreground-800 hover:bg-background-200/50"
                    >
                      再发一条
                    </button>
                  </div>
                ) : (
                  <form id="contact-form" data-readdy-form="contact-form" onSubmit={handleSubmit} noValidate>
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-800">
                          你的称呼 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="name"
                          name="name"
                          type="text"
                          required
                          maxLength={50}
                          placeholder="怎么称呼你"
                          className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-800">
                          邮箱 <span className="text-primary-500">*</span>
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          maxLength={100}
                          placeholder="your@email.com"
                          className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                        />
                      </div>
                    </div>
                    <div className="mt-5">
                      <label htmlFor="topic" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        主题
                      </label>
                      <select
                        id="topic"
                        name="topic"
                        className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                      >
                        <option value="内容合作">内容合作</option>
                        <option value="功能建议">功能建议</option>
                        <option value="问题反馈">问题反馈</option>
                        <option value="其他">其他</option>
                      </select>
                    </div>
                    <div className="mt-5">
                      <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-800">
                        留言内容 <span className="text-primary-500">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={5}
                        placeholder="请简单描述你的需求或建议（最多 500 字）"
                        className="w-full resize-none rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
                      />
                    </div>

                    {/* Anti-spam honeypot */}
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="website_alt">网站</label>
                      <input
                        id="website_alt"
                        name="website_alt"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        readOnly
                      />
                    </div>

                    {status === "error" && formError && (
                      <p className="mt-4 rounded-md bg-primary-100 px-4 py-2.5 text-sm text-primary-800" role="alert">
                        <i className="ri-error-warning-line mr-1" />
                        {formError}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      {submitting ? (
                        <>
                          <i className="ri-loader-4-line animate-spin" /> 提交中...
                        </>
                      ) : (
                        <>
                          提交信息 <i className="ri-send-plane-line" />
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}